import type { TFunction } from 'i18next';
import type { AnswerInput, RoundAnswer, RoundProblemRef, RoundResult, RoundView } from '../../../api/safetyRounds';
import { Routes } from '../../../enums/Routes';

export const roundPath = (id: number) => Routes.SafetyRepRound.replace(':roundId', String(id));

/** The general manager walks a round in their own part of the app, never in the safety representative's */
export const managerRoundPath = (id: number) => Routes.GeneralManagerRound.replace(':roundId', String(id));

/**
 * The reader works with safety rounds as general manager: they came from the general manager's pages, or they are
 * general manager without being a safety representative, who never sees the safety representative's part
 */
export const inManagerArea = (roles: string[], pathname: string) =>
  pathname.startsWith('/daglig-leder/') || (!roles.includes('SAFETY_REP') && roles.includes('GENERAL_MANAGER'));

/** The round where the reader works with safety rounds */
export const roundPathFor = (id: number, roles: string[], pathname: string) =>
  inManagerArea(roles, pathname) ? managerRoundPath(id) : roundPath(id);

/** «Vernerunder» in the breadcrumb: the plans of the safety representative, or the overview of the others */
export const roundsHomeFor = (roles: string[], pathname = '') =>
  inManagerArea(roles, pathname)
    ? Routes.GeneralManagerSafetyRoundOverview
    : roles.includes('SAFETY_REP')
      ? Routes.SafetyRepRoundPlans
      : roles.includes('HR_ADMIN')
        ? Routes.HRSafetyRoundOverviewFullPath
        : Routes.SafetyRepRoundPlans;

/**
 * A general manager who is not a safety representative never lands in the safety representative's part of the app.
 * Where a link or an old email takes them there, they go to the same page in their own part, or to the start.
 * The PDF pages are rendered by the server and are left as they are.
 */
export const managerPathFor = (pathname: string, search: string, roles: string[]): string | null => {
  if (!pathname.startsWith('/verneombud') || !inManagerArea(roles, '')) return null;
  if (new URLSearchParams(search).get('pdf') === 'true') return null;
  const match = (pattern: RegExp) => pathname.match(pattern)?.[1];
  const protocol = match(/^\/verneombud\/vernerunder\/runde\/(\d+)\/protokoll\/?$/);
  if (protocol) return Routes.GeneralManagerProtocol.replace(':roundId', protocol) + search;
  const round = match(/^\/verneombud\/vernerunder\/runde\/(\d+)\/?$/);
  if (round) return managerRoundPath(Number(round));
  const plan = match(/^\/verneombud\/vernerunder\/plan\/(\d+)(?:\/sjekkliste)?\/?$/);
  if (plan) return Routes.GeneralManagerPlan.replace(':planId', plan);
  if (/^\/verneombud\/vernerunder\/varsler\/?$/.test(pathname)) return Routes.GeneralManagerNotifications;
  if (pathname.startsWith('/verneombud/vernerunder')) return Routes.GeneralManagerSafetyRoundOverview;
  return Routes.Dashboard;
};

/** The three choices on each checkpoint. «Annet» is not checked or not relevant, chosen in a menu. */
export type Choice = 'OK' | 'DEVIATION' | 'OTHER';

export const CHOICES: Choice[] = ['OK', 'DEVIATION', 'OTHER'];

export const OTHER_RESULTS: Array<Extract<RoundResult, 'NOT_CHECKED' | 'NOT_RELEVANT'>> = ['NOT_CHECKED', 'NOT_RELEVANT'];

export const choiceOf = (result: RoundResult | null): Choice | null =>
  result === 'OK' || result === 'DEVIATION' ? result : result ? 'OTHER' : null;

/** An answer while it is being written. Empty fields are kept as text until saved. */
export type DraftAnswer = {
  result: RoundResult | null;
  comment: string;
  description: string;
  responsibleId: number | null;
  dueDate: string;
};

export const EMPTY_ANSWER: DraftAnswer = { result: null, comment: '', description: '', responsibleId: null, dueDate: '' };

export const toDraftAnswer = (answer: RoundAnswer | null): DraftAnswer =>
  answer
    ? {
        result: answer.result,
        comment: answer.comment ?? '',
        description: answer.description ?? '',
        responsibleId: answer.responsibleId,
        dueDate: answer.dueDate ?? '',
      }
    : EMPTY_ANSWER;

export const initialAnswers = (round: Pick<RoundView, 'categories'>): Record<number, DraftAnswer> =>
  Object.fromEntries(
    round.categories.flatMap((category) => category.items.map((item) => [item.id, toDraftAnswer(item.answer)])),
  );

export const toAnswerInput = (draft: DraftAnswer): AnswerInput =>
  draft.result === null
    ? { result: null }
    : {
        result: draft.result,
        comment: draft.comment.trim() || null,
        description: draft.result === 'DEVIATION' ? draft.description.trim() || null : null,
        responsibleId: draft.result === 'DEVIATION' ? draft.responsibleId : null,
        dueDate: draft.result === 'DEVIATION' ? draft.dueDate || null : null,
      };

type Items = Pick<RoundView, 'categories'>;

/** Counted from what is on the screen, so the bar moves as soon as an answer is given */
export const progressOf = (round: Items, answers: Record<number, DraftAnswer>) => {
  const all = round.categories.flatMap((category) => category.items.map((item) => answers[item.id] ?? EMPTY_ANSWER));
  return {
    done: all.filter((answer) => answer.result).length,
    total: all.length,
    deviations: all.filter((answer) => answer.result === 'DEVIATION').length,
  };
};

/** How far a category is: answered, all, and deviations, for its tab and heading */
export const categoryProgress = (items: Array<{ id: number }>, answers: Record<number, DraftAnswer>) => {
  const results = items.map((item) => answers[item.id]?.result ?? null);
  return {
    done: results.filter(Boolean).length,
    total: results.length,
    deviations: results.filter((result) => result === 'DEVIATION').length,
  };
};

/** One segment of a category's bar per checkpoint: green OK, red deviation, dark for the others, light when open */
export type Segment = 'ok' | 'deviation' | 'other' | 'open';

export const segmentsOf = (items: Array<{ id: number }>, answers: Record<number, DraftAnswer>): Segment[] =>
  items.map((item) => {
    const result = answers[item.id]?.result ?? null;
    return result === 'OK' ? 'ok' : result === 'DEVIATION' ? 'deviation' : result ? 'other' : 'open';
  });

/** «Resten er OK»: the checkpoints of the category not answered yet */
export const unansweredIn = (items: Array<{ id: number }>, answers: Record<number, DraftAnswer>) =>
  items.filter((item) => !answers[item.id]?.result).map((item) => item.id);

/** Where the round opens: the first category with something left, and its first open checkpoint */
export const startingPoint = (round: Items, answers: Record<number, DraftAnswer>) => {
  const index = round.categories.findIndex((category) => unansweredIn(category.items, answers).length > 0);
  const category = round.categories[Math.max(index, 0)];
  return {
    categoryIndex: Math.max(index, 0),
    itemId: (category && (unansweredIn(category.items, answers)[0] ?? category.items[0]?.id)) ?? null,
  };
};

/** The category a checkpoint belongs to, to show it when a problem points there */
export const categoryOfItem = (round: Items, itemId: number) =>
  round.categories.findIndex((category) => category.items.some((item) => item.id === itemId));

/** «10:42», when the round was last saved */
export const savedTime = (at: string, language: string) =>
  new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', { hour: '2-digit', minute: '2-digit' }).format(
    new Date(at),
  );

export type RoundProblem = { itemId: number; fieldId: string; message: string };

export const itemFieldId = (itemId: number) => `round-item-${itemId}`;
export const reasonFieldId = (itemId: number) => `round-reason-${itemId}`;
export const descriptionFieldId = (itemId: number) => `round-description-${itemId}`;

/** What stops the round from being finished, linked to the field: every answer, a reason and a description */
const PROBLEM_KEYS: Record<RoundProblemRef['type'], string> = {
  unanswered: 'unanswered',
  notCheckedReason: 'reason',
  deviationDescription: 'description',
};

/** «Beskriv avviket i punkt 1.1»: what a checkpoint lacks, the same on the round and the protocol */
export const problemMessage = (problem: Pick<RoundProblemRef, 'type' | 'number'>, t: TFunction) =>
  t(`safetyRounds.round.errors.${PROBLEM_KEYS[problem.type]}`, { number: problem.number });

export const roundProblems = (round: Items, answers: Record<number, DraftAnswer>, t: TFunction): RoundProblem[] =>
  round.categories.flatMap((category) =>
    category.items.flatMap((item): RoundProblem[] => {
      const answer = answers[item.id] ?? EMPTY_ANSWER;
      const at = { itemId: item.id, number: item.number };
      if (!answer.result)
        return [{ itemId: item.id, fieldId: itemFieldId(item.id), message: problemMessage({ ...at, type: 'unanswered' }, t) }];
      if (answer.result === 'NOT_CHECKED' && !answer.comment.trim())
        return [{ itemId: item.id, fieldId: reasonFieldId(item.id), message: problemMessage({ ...at, type: 'notCheckedReason' }, t) }];
      if (answer.result === 'DEVIATION' && !answer.description.trim())
        return [
          { itemId: item.id, fieldId: descriptionFieldId(item.id), message: problemMessage({ ...at, type: 'deviationDescription' }, t) },
        ];
      return [];
    }),
  );

export type SaveState = 'saved' | 'saving' | 'retrying';

/**
 * The queue of answers waiting to be saved. Each checkpoint has at most one request in flight;
 * a change while it is saving is sent when it is done, so the last answer always wins.
 */
export type SaveQueue = {
  pending: Record<number, AnswerInput>;
  inFlight: number[];
  failed: boolean;
};

export const EMPTY_QUEUE: SaveQueue = { pending: {}, inFlight: [], failed: false };

export const enqueue = (queue: SaveQueue, itemId: number, input: AnswerInput): SaveQueue => ({
  ...queue,
  pending: { ...queue.pending, [itemId]: input },
});

/** The answers that can be sent now: pending and not already on their way */
export const ready = (queue: SaveQueue) =>
  Object.keys(queue.pending)
    .map(Number)
    .filter((itemId) => !queue.inFlight.includes(itemId));

export const sending = (queue: SaveQueue, itemId: number): { queue: SaveQueue; input: AnswerInput } => {
  const { [itemId]: input, ...rest } = queue.pending;
  return { queue: { ...queue, pending: rest, inFlight: [...queue.inFlight, itemId] }, input };
};

export const sent = (queue: SaveQueue, itemId: number): SaveQueue => {
  const inFlight = queue.inFlight.filter((id) => id !== itemId);
  return { ...queue, inFlight, failed: queue.failed && Object.keys(queue.pending).length > 0 };
};

/** A failed answer goes back in the queue, unless a newer one is already waiting */
export const failed = (queue: SaveQueue, itemId: number, input: AnswerInput): SaveQueue => ({
  pending: itemId in queue.pending ? queue.pending : { ...queue.pending, [itemId]: input },
  inFlight: queue.inFlight.filter((id) => id !== itemId),
  failed: true,
});

export const saveState = (queue: SaveQueue): SaveState =>
  queue.failed ? 'retrying' : queue.inFlight.length > 0 || Object.keys(queue.pending).length > 0 ? 'saving' : 'saved';
